import { apiPostRequest } from "helpers/Requests";
import { USER_ROLE } from "Constants/constant";
import {
  canAccessRosteringLeaveRequests,
  isFrontlineEmployeeUser,
} from "helpers/RolesPermission";

export const PENDING_LEAVE_CHANGED_EVENT = "pending-leave-changed";

/** Re-use count within this window unless explicitly invalidated. */
const STALE_MS = 5 * 60 * 1000;

let cachedCount = 0;
let lastFetchedAt = 0;
let inflight = null;
const countListeners = new Set();

const canFetchPendingLeaveCount = () => {
  if (isFrontlineEmployeeUser()) return false;
  const userType = localStorage.getItem("userType");
  const isSpManagerOrAdmin =
    userType === USER_ROLE.Sp_Admin || userType === USER_ROLE.Sp_manager;
  if (!isSpManagerOrAdmin) return false;
  return canAccessRosteringLeaveRequests();
};

const emitCount = (count) => {
  cachedCount = Number.isFinite(count) ? count : 0;
  countListeners.forEach((listener) => {
    try {
      listener(cachedCount);
    } catch (e) {
      // ignore listener errors
    }
  });
};

const parsePendingLeaveTotal = (res) => {
  const leaves =
    res?.data?.data?.leaves ||
    res?.data?.leaves ||
    null;

  if (!leaves) return 0;

  const total = Number(
    leaves.total ?? leaves.meta?.total ?? leaves.data?.length ?? 0
  );
  return Number.isFinite(total) ? total : 0;
};

/**
 * Fetch latest pending (non-expired) leave count and push to subscribers.
 * SP Admin / Manager with leave-request access only — never call from worker portal.
 * @param {{ force?: boolean }} [options] force skips TTL and always hits the API
 */
export const refreshPendingLeaveCount = async (options = {}) => {
  const { force = false } = options;

  if (!canFetchPendingLeaveCount()) {
    return cachedCount;
  }

  if (inflight) return inflight;

  if (
    !force &&
    lastFetchedAt &&
    Date.now() - lastFetchedAt < STALE_MS
  ) {
    return cachedCount;
  }

  inflight = (async () => {
    try {
      const res = await apiPostRequest("sp/worker-requested-leaves", {
        filter: { status: ["pending"] },
        page: 1,
        per_page: 1,
      });

      if (res?.status === 200) {
        const total = parsePendingLeaveTotal(res);
        lastFetchedAt = Date.now();
        emitCount(total);
        return total;
      }
    } catch (err) {
      console.error("Error fetching pending leave count", err);
    } finally {
      inflight = null;
    }
    return cachedCount;
  })();

  return inflight;
};

let teardownLifecycle = null;

/**
 * Single app-level listener for badge refresh (tab focus / visibility).
 * Safe to call on login or permission refresh; avoids per-page SideBar remount fetches.
 */
export const detachPendingLeaveBadgeLifecycle = () => {
  teardownLifecycle?.();
};

export const ensurePendingLeaveBadgeLifecycle = () => {
  if (typeof window === "undefined") return;

  if (!canFetchPendingLeaveCount()) {
    return;
  }

  if (teardownLifecycle !== null) {
    return;
  }

  refreshPendingLeaveCount();

  const refreshWhenVisible = () => {
    if (document.visibilityState === "hidden") return;
    refreshPendingLeaveCount();
  };

  const onPermissionsRefresh = () => {
    if (!canFetchPendingLeaveCount()) {
      detachPendingLeaveBadgeLifecycle();
      return;
    }
    refreshPendingLeaveCount();
  };

  window.addEventListener("focus", refreshWhenVisible);
  document.addEventListener("visibilitychange", refreshWhenVisible);
  window.addEventListener("refreshPermissions", onPermissionsRefresh);

  teardownLifecycle = () => {
    window.removeEventListener("focus", refreshWhenVisible);
    document.removeEventListener("visibilitychange", refreshWhenVisible);
    window.removeEventListener("refreshPermissions", onPermissionsRefresh);
    teardownLifecycle = null;
  };
};

/** Tear down listeners and clear cached badge (e.g. on logout). */
export const stopPendingLeaveBadgeLifecycle = () => {
  detachPendingLeaveBadgeLifecycle();
  lastFetchedAt = 0;
  cachedCount = 0;
  emitCount(0);
};

export const getPendingLeaveCount = () => cachedCount;

/**
 * Subscribe to count updates. Immediately receives current cached value.
 */
export const subscribePendingLeaveCount = (listener) => {
  if (typeof listener !== "function") return () => {};

  const isFirstSubscriber = countListeners.size === 0;
  countListeners.add(listener);
  listener(cachedCount);

  if (isFirstSubscriber && canFetchPendingLeaveCount()) {
    ensurePendingLeaveBadgeLifecycle();
  }

  return () => {
    countListeners.delete(listener);
    if (countListeners.size === 0) {
      detachPendingLeaveBadgeLifecycle();
    }
  };
};

/**
 * Call after leave create / update / delete / approve / reject.
 * Refreshes count immediately for SP users only.
 */
export const notifyPendingLeaveChanged = () => {
  if (!canFetchPendingLeaveCount()) return;

  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PENDING_LEAVE_CHANGED_EVENT));
  }
  refreshPendingLeaveCount({ force: true });
};

export const subscribePendingLeaveChanged = (handler) => {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(PENDING_LEAVE_CHANGED_EVENT, handler);
  return () => window.removeEventListener(PENDING_LEAVE_CHANGED_EVENT, handler);
};
